import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 4.1 visual preview at /preview/lesson/4.1
 * "The five moves only you can make"
 */

export default function LessonPreviewPage4_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 4 · Doing the work AI cannot do · Lesson 4.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The five moves only you can make
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Judgment · Voice · Character</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 missing-move drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="A paragraph that is true and useless.">
      <p className="mt-6 text-text/90">
        A student asks AI for a paragraph on the new phone policy at their school.
        AI returns this:
      </p>
      <div className="mt-5 rounded-lg border border-line bg-surface p-5">
        <p className="text-text/90 italic">
          &ldquo;The new phone policy at our school requires students to keep their
          phones in lockers during the school day. The policy was implemented to
          reduce distractions and improve focus. Many students and teachers have
          opinions on the policy. Like any policy, there are benefits and
          drawbacks. The school is monitoring the effects to determine if changes
          are needed.&rdquo;
        </p>
      </div>
      <p className="mt-6 text-text/90">
        Every sentence is true. Nothing is wrong. There is also nothing in it.
        A reader walks away knowing exactly what they knew before. AI did the
        research, AI did the drafting, AI did a clean two-pass audit. The
        paragraph is shippable by every test from Week 3.
      </p>
      <p className="mt-4 text-text/90">
        It is also useless. Because the paragraph does not say which part of the
        policy actually changed anyone&apos;s day, what a real student or teacher
        said about it, why this matters more than five other policies, or what
        the writer thinks anyone should do about it.
      </p>
      <p className="mt-4 text-text/90">
        AI did not know to ask any of those questions. That is the job of the
        person at the keyboard. Week 4 is about that job.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Week 3" title="The chain works. Now name what AI cannot do inside it.">
      <p className="mt-6 text-text/90">
        Week 3 gave you a chain: research, draft, audit, ship. Every link has a
        move. Every link has a check. The chain works.
      </p>
      <p className="mt-4 text-text/90">
        The chain does not run itself. Someone has to decide what the work is
        about, notice what is specific, defend what got shipped. AI cannot do any
        of those. Week 4 names those moves so you can practice each one.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Name the four links of the chain in order." />
        <ReviewItem n="02" q="Define Provenance in one sentence." />
        <ReviewItem n="03" q="What is the difference between drafting with AI and AI drafting for you?" />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The five moves" title="What only you can do. Name them, then practice them one by one.">
      <Subhead n={1} title="The vocab for the meta-skill" />
      <p className="mt-4 text-text/90">
        There is one word that holds the whole week together. Without it, the five
        moves feel like a list of tips. With it, they become one skill with five
        sides.
      </p>
      <VocabCard
        term="Human-in-the-loop"
        definition="A working pattern where AI generates and a person decides. The human is not optional and not a rubber stamp. The human is the part of the system that catches what is wrong, decides what matters, and takes the name on the work. Every move in Week 4 is one face of this pattern."
        analogy="A coach watching practice sees the same drill ten kids run. The coach decides who needs more reps, who is hiding, who is about to get good. The information is the same for everyone watching. The decision is not. AI can be the watcher. AI cannot be the coach. Human-in-the-loop is naming who is the coach."
        memoryHook="AI generates. You decide. If the deciding part is missing, AI is not faster. It is just louder."
        why="This is the phrase the whole field uses for what you are doing every time you work with AI. Knowing the term means you can talk about your work the way professionals do, and you can recognize when a system has cut the human out (and decide whether that is okay). Every move in Week 4 is a piece of being the human in the loop."
      />

      <Subhead n={2} title="The five moves" className="mt-12" />
      <p className="mt-4 text-text/90">
        Five things AI cannot do for you. The rest of Week 4 takes them one at a
        time. Today, see them as a set.
      </p>
      <MovesGrid />

      <Subhead n={3} title="Why AI cannot do any of these" className="mt-12" />
      <p className="mt-4 text-text/90">
        Each move requires something AI does not have. Not because the model is
        weak. Because the move is about you in particular, or this reader in
        particular, or this moment in particular.
      </p>
      <ReasonsGrid />

      <Subhead n={4} title="The pattern to spot" className="mt-12" />
      <p className="mt-4 text-text/90">
        When work feels flat but you cannot say why, one of the five moves is
        missing. The cold-open paragraph was missing all five. You will start to
        notice.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The diagnostic
        </p>
        <p className="mt-3 text-text/90">
          Read the work out loud. Ask: what is this about? what is in here that I
          could not find on the first page of a search? what would I say if a
          teacher asked &ldquo;why did you write that sentence?&rdquo; who is the
          one reader I want to land this with? If any answer is fuzzy, the matching
          move is missing.
        </p>
      </div>
    </Section>
  );
}

const MOVES = [
  {
    n: '01',
    name: 'Decide what matters',
    accent: 'emerald' as const,
    body: 'Choose the one thing the work is actually about. Cut everything that does not serve it. This is the editing move. Lesson 4.2 goes deep.',
  },
  {
    n: '02',
    name: 'Notice what is specific',
    accent: 'amber' as const,
    body: 'Find the detail only available to someone who was actually there. AI generalizes. You particularize. Lesson 4.3 goes deep.',
  },
  {
    n: '03',
    name: 'Connect what does not obviously connect',
    accent: 'emerald' as const,
    body: 'See the line between two things that look unrelated. Original ideas live in those lines. AI finds connections that already exist in its training. You find the ones that do not yet.',
  },
  {
    n: '04',
    name: 'Defend what you shipped',
    accent: 'amber' as const,
    body: 'Answer for every claim, every paragraph, every change you accepted. Trace it back to a real source or a real choice. Lesson 4.4 goes deep.',
  },
  {
    n: '05',
    name: 'Care about who reads it',
    accent: 'emerald' as const,
    body: 'Know the one real person you wrote this for. Write so it lands with them. AI does not know your audience. You do. Lesson 4.5 puts all five together.',
  },
];

function MovesGrid() {
  return (
    <div className="mt-6 space-y-4">
      {MOVES.map((m) => (
        <MoveCard key={m.n} move={m} />
      ))}
    </div>
  );
}

function MoveCard({ move }: { move: (typeof MOVES)[number] }) {
  const isEmerald = move.accent === 'emerald';
  const borderCls = isEmerald ? 'border-emerald-500/40' : 'border-amber/40';
  const bgCls = isEmerald ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
  const labelCls = isEmerald ? 'text-emerald-300' : 'text-amber';
  return (
    <div className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
      <div className="flex flex-wrap items-baseline gap-3">
        <span className="font-mono text-xs text-muted">Move {move.n}</span>
        <h4 className={`text-lg font-semibold ${labelCls}`}>{move.name}</h4>
      </div>
      <p className="mt-3 text-text/90">{move.body}</p>
    </div>
  );
}

const REASONS = [
  {
    n: '01',
    label: 'AI was not there',
    body: 'AI was not at your basketball game, in your kitchen, in your group chat, on your bus. The specifics that make work feel real come from being somewhere. AI has read about being there. That is not the same.',
  },
  {
    n: '02',
    label: 'AI does not know your reader',
    body: 'AI does not know your younger brother, your bio teacher, your coach. AI optimizes for the average reader. You write for the actual one.',
  },
  {
    n: '03',
    label: 'AI cannot stand behind it',
    body: 'A teacher cannot ask AI why a sentence is there. They can ask you. If your answer is “AI wrote it,” you have not shipped work. You have laundered it.',
  },
];

function ReasonsGrid() {
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-3">
      {REASONS.map((r) => (
        <div key={r.n} className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            {r.n} · {r.label}
          </p>
          <p
            className="mt-3 text-sm text-text/85"
            dangerouslySetInnerHTML={{ __html: r.body }}
          />
        </div>
      ))}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Find the missing move" title="Three flat outputs. Pick the move that is missing.">
      <p className="mt-6 text-text/90">
        For each excerpt, pick which of the five moves is missing. The output is
        not wrong. The output is empty. Name the gap.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <MovesDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

const MOVE_OPTIONS = [
  { key: 'matters', label: 'Decide what matters', short: 'Matters' },
  { key: 'specific', label: 'Notice what is specific', short: 'Specific' },
  { key: 'connect', label: 'Connect what does not connect', short: 'Connect' },
  { key: 'defend', label: 'Defend what you shipped', short: 'Defend' },
  { key: 'reader', label: 'Care about who reads it', short: 'Reader' },
];

type DrillSpec = {
  n: string;
  excerpt: string;
  context: string;
  correct: string;
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    context:
      'Opening of a college application essay about playing on the school basketball team.',
    excerpt:
      '“Basketball has taught me many valuable lessons. Through dedication, teamwork, and perseverance, I have grown as a player and as a person. The sport has shaped who I am today.”',
    correct: 'specific',
    verdict:
      'Notice what is specific. Every sentence is true for every player on every team in every sport. There is no moment, no game, no person, no number. AI wrote a version of basketball that has never been played. The fix is one specific second: a timeout you called, a teammate\'s face after a loss, the score when something shifted. One particular detail does more than the whole paragraph above.',
  },
  {
    n: '02',
    context:
      'A 2-page essay arguing that the school start time should be moved later. The paragraph below is the second paragraph.',
    excerpt:
      '“Studies have shown that adolescent sleep patterns differ from adult sleep patterns. School start times can affect student performance. Many districts have considered changes. Sleep is an important factor in academic success.”',
    correct: 'defend',
    verdict:
      'Defend what you shipped. Every sentence makes a claim. None of the claims point at a specific study, a specific district, or a specific number. If a teacher asks “which studies?” the writer has nothing to say. Defensible means traceable. This paragraph reads like research and is actually a row of empty signs.',
  },
  {
    n: '03',
    context:
      'A reflection a student wrote for a coach after the season ended. The coach is the only reader.',
    excerpt:
      '“This season was a great learning experience. I improved my skills, built relationships with my teammates, and gained valuable insights into the game of basketball. I look forward to continuing my development.”',
    correct: 'reader',
    verdict:
      'Care about who reads it. The coach knows every game, every practice, every drill the writer ran. Writing in general terms to a person who knows the specifics is the opposite of writing for the reader. A real letter to this coach starts with one moment from one practice the coach would also remember. The audience is one person. Treat them like one person.',
  },
];

function MovesDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<string | null>(null);
  const correctMeta = MOVE_OPTIONS.find((o) => o.key === drill.correct);
  const pickedMeta = MOVE_OPTIONS.find((o) => o.key === picked);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The context
      </p>
      <p className="mt-3 text-text/90">{drill.context}</p>
      <div className="mt-4 rounded-md border border-line bg-elevated p-4">
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted">
          The excerpt
        </p>
        <p
          className="mt-2 text-text/90 italic"
          dangerouslySetInnerHTML={{ __html: drill.excerpt }}
        />
      </div>
      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which move is missing?
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {MOVE_OPTIONS.map((opt) => {
            const isPicked = picked === opt.key;
            const isCorrect = opt.key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-3 text-left text-sm text-text/85 hover:border-amber';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-3 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-3 text-left text-sm text-text';
            return (
              <button
                key={opt.key}
                className={cls}
                onClick={() => setPicked(opt.key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Move · {opt.short}
                </span>
                <span className="mt-1 block">{opt.label}</span>
              </button>
            );
          })}
        </div>
        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · {pickedMeta?.label}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That is not the move that is missing here. See the correct answer
              below.
            </p>
          </div>
        )}
        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The missing move' : 'Correct'} · {correctMeta?.label}
            </p>
            <p
              className="mt-2 text-sm text-text/90"
              dangerouslySetInnerHTML={{ __html: drill.verdict }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Judgment in one sentence." answer="The act of deciding what matters, what is specific, what connects, what holds up under questioning, and who the work is for: the thing that turns information into work that does something." />
        <QuickCheckItem n="Q2" q="Name the five moves only you can make." answer="Decide what matters. Notice what is specific. Connect what does not obviously connect. Defend what you shipped. Care about who reads it." />
        <QuickCheckItem n="Q3" q="The difference between opinion, guess, and judgment, in one sentence each." answer="Opinion is what you think. Guess is what you hope. Judgment is what you decide and can defend." />
        <QuickCheckItem n="Q4" q="Work feels flat but you cannot say why. What is the diagnostic?" answer="Read it out loud and ask: what is this about, what is in here you could not find on the first page of search, why did you write that sentence, who is the one reader. Any fuzzy answer points at the missing move." />
        <QuickCheckItem n="Q5" q="Why can AI not do any of these moves, in one sentence?" answer="Each move depends on you in particular, this reader in particular, or this moment in particular: things AI does not have access to, no matter how good the model gets." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The five-move map for the week.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 4.1 · Lock card · The five moves
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          AI generates. You decide. The deciding is the whole job and it has five
          faces.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <FiveTile n="01" name="Matters" body="Decide what the work is about. Cut what does not serve it." />
          <FiveTile n="02" name="Specific" body="Find the detail only you have. Replace AI averages with particulars." />
          <FiveTile n="03" name="Connect" body="See the line between two things that look unrelated. Original lives there." />
          <FiveTile n="04" name="Defend" body="Trace every claim. Answer for every paragraph. Stand behind every change." />
          <FiveTile n="05" name="Reader" body="Know the one person you wrote this for. Land it with them." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The check before you submit
          </p>
          <p className="mt-2 text-text/90">
            Run the five questions: what is this about, what is in here only I
            could put there, what does this connect that I have not seen
            connected, why did I write each sentence, who is the one reader. If
            any answer is fuzzy, the matching move is the next thing to fix.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Judgment is the meta-skill. The five moves are how you practice it.
        </p>
      </div>
    </Section>
  );
}

function FiveTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 4.2
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Decide what matters: the editing move
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Most of writing is deciding what to cut. AI cannot do this for you
            because AI does not know what you are trying to do. Vocab: Through-line.
            The cut 30% exercise.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 4.1 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p className="mt-3 border-t border-line pt-3 text-sm text-text/85">
          {answer}
        </p>
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From Week 3',
    'The five moves',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
